import type { Map } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import getCurrentPosition from '@alltrails/core/utils/getCurrentPosition';
import IconButton from '@alltrails/denali/components/IconButton';
import Location from '@alltrails/denali/icons/Location';
import Modal from '@alltrails/modal/components/Modal/Modal';
import CurrentMarker from '../components/CurrentMarker';
import { LngLat } from '../types/Geo';
import isLocationVisible from '../utils/isLocationVisible';
import useMap from './useMap';

const useCurrentLocationButton = (onClick?: () => void) => {
  const intl = useIntl();
  const map = useMap();
  const [locationStatus, setLocationStatus] = useState<'allowed' | 'denied' | 'loading' | 'unknown'>('unknown');
  const [currentLocation, setCurrentLocation] = useState<LngLat>();
  const [isCurrentLocationVisible, setIsCurrentLocationVisible] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  useEffect(() => {
    if (!currentLocation || !map) {
      return;
    }

    const onMoveEnd = (event: { target: Map }) => {
      setIsCurrentLocationVisible(isLocationVisible(event.target, currentLocation));
    };
    map.on('moveend', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
    };
  }, [currentLocation, map]);

  const currentLocationMarker = useMemo(() => {
    if (currentLocation) {
      return <CurrentMarker latitude={currentLocation[1]} longitude={currentLocation[0]} />;
    }
    return null;
  }, [currentLocation]);

  const onButtonClick = useCallback(() => {
    onClick?.();
    switch (locationStatus) {
      case 'allowed':
        if (currentLocation) {
          map?.flyTo({ center: currentLocation });
        }
        break;
      case 'denied':
        setShowLocationModal(true);
        break;
      case 'unknown':
        setLocationStatus('loading');
        getCurrentPosition(
          position => {
            setLocationStatus('allowed');
            const location: LngLat = [position.coords.longitude, position.coords.latitude];
            setCurrentLocation(location);
            map?.flyTo({ center: location });
          },
          () => {
            setLocationStatus('denied');
            setShowLocationModal(true);
          }
        );
      case 'loading':
      default:
        break;
    }
  }, [currentLocation, locationStatus, map, onClick]);

  const currentLocationButton = useMemo(() => {
    // We show the button when the user hasn't given location service permissions OR when the user's current location isn't visible on the map
    const showButton = locationStatus !== 'allowed' || !isCurrentLocationVisible;
    return showButton ? (
      <IconButton
        style={locationStatus === 'denied' ? { opacity: 0.4 } : undefined}
        icon={{ Component: Location }}
        loading={locationStatus === 'loading'}
        onClick={onButtonClick}
        title={intl.formatMessage({ defaultMessage: 'Current location' })}
        variant="ghost"
        elevated
      />
    ) : null;
  }, [intl, isCurrentLocationVisible, locationStatus, onButtonClick]);

  const currentLocationModal = useMemo(() => {
    return (
      <Modal
        isOpen={showLocationModal}
        onCloseRequest={() => setShowLocationModal(false)}
        title={intl.formatMessage({ defaultMessage: 'Turn on location sharing' })}
      >
        <Typography component="div" style={{ padding: 'var(--space-200) 0' }} variant="text200">
          <FormattedMessage defaultMessage="To show trails near your current location, turn on location sharing in your browser settings, then refresh the page." />
        </Typography>
      </Modal>
    );
  }, [intl, showLocationModal]);

  return useMemo(
    () => ({ currentLocationMarker, currentLocationButton, currentLocationModal }),
    [currentLocationMarker, currentLocationButton, currentLocationModal]
  );
};

export default useCurrentLocationButton;
